import axios from 'axios';
import type { Stripe } from '@stripe/stripe-js';
import { webApiClient } from '../../lib/fi-api/apiUtils';
import { UserFacingError, getCustomerMessageFromApolloError } from '../../lib/errors';
import type { BillingDetailsPrefill } from '../../views/ManageSubscription/Web/RecoverPayment/RecoverPaymentForm';

export const UPDATE_PAYMENT_METHOD_PATH = '/api/ecommerce/update-payment-method';

interface UpdatePaymentMethodError {
  code?: string;
  clientSecret?: string;
  setupIntentId?: string;
}

export function parseUpdatePaymentMethodError(err: unknown): UpdatePaymentMethodError | undefined {
  if (!axios.isAxiosError(err)) return undefined;
  const error = (err.response?.data as { error?: UpdatePaymentMethodError } | undefined)?.error;
  return error ?? {};
}

export function accountEditErrorMessage(err: unknown, t: (key: string) => string): string {
  const restError = parseUpdatePaymentMethodError(err);
  if (restError) {
    return t(
      restError.code === 'cannot_update_payment_info'
        ? 'membership_billed_by_another_account'
        : 'update_payment_method_error',
    );
  }
  if (err instanceof UserFacingError) return err.message;
  return getCustomerMessageFromApolloError(err) ?? t('something_went_wrong');
}

/**
 * The endpoint confirms a SetupIntent server-side and stores the resulting payment method on the
 * account. A card that needs 3DS comes back as a 400 carrying the client secret; the challenge runs
 * in the browser and the finished SetupIntent is then posted back to complete the save.
 */
export async function saveStripePaymentMethod(stripe: Stripe, confirmationToken: string) {
  try {
    await webApiClient.post(UPDATE_PAYMENT_METHOD_PATH, { confirmationToken });
  } catch (err) {
    const restError = parseUpdatePaymentMethodError(err);
    if (restError?.code !== 'setup_intent_requires_action' || !restError.clientSecret) throw err;
    const { error } = await stripe.handleNextAction({ clientSecret: restError.clientSecret });
    if (error) throw new UserFacingError(error.message);
    await webApiClient.post(UPDATE_PAYMENT_METHOD_PATH, { setupIntentId: restError.setupIntentId });
  }
}

interface PrefillAddress {
  line1: string;
  line2?: string | null;
  city: string;
  state: string;
  zip: string;
  country: string;
}

interface PrefillName {
  firstName?: string | null;
  lastName?: string | null;
}

interface PrefillAccount extends PrefillName {
  address?: PrefillAddress | null;
  billingInfo?: (PrefillName & { address?: PrefillAddress | null }) | null;
}

function fullName(name: PrefillName | null | undefined) {
  return [name?.firstName, name?.lastName].filter(Boolean).join(' ') || undefined;
}

/** the stored billing details first, then the shipping address, so a member only types the card */
export function billingDetailsPrefillFromAccount(
  account: PrefillAccount | null | undefined,
  country: string,
): BillingDetailsPrefill {
  const address = account?.billingInfo?.address ?? account?.address;
  return {
    name: fullName(account?.billingInfo) ?? fullName(account),
    address: address
      ? {
          line1: address.line1,
          line2: address.line2 ?? undefined,
          city: address.city,
          state: address.state,
          postal_code: address.zip,
          country: address.country,
        }
      : { line1: '', country },
  };
}
